import {
  BankOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  EditOutlined,
  FileOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons'
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Progress,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'
import { blue } from '../../color'
import AccessControlAction from '../../components/AccessControlAction'
import { AvatarHasName } from '../../components/Common/Avatar'
import FileItem from '../../components/Common/FileItemComponent/FileItem'
import UploadFileItem from '../../components/Common/FileItemComponent/UploadFileItem'
import ContentBlockWrapper from '../../components/ContentBlockWrapper'
import PopoverWatchTime from '../../components/PopoverWatchTime'
import SelectPeoplePopup from '../../components/SelectPeoplePopup/SelectPeoplePopup'
import {
  ACL_ACTION_TYPE,
  ASSIGNEE_TYPE,
  DATE_FORMAT_DEFAULT,
  VIEWLOG_DATE,
} from '../../constants'
import { KpiV2Request } from '../../requests/KpiV2Request'
import utils from '../../utils'
import {
  SelectList,
  SelectTitle,
} from '../Documents/InternalDocumentIncomingDocCreatePage/InternalDocumentIncomingDocCreatePageStyled'

const { TextArea } = Input
const { Option } = Select
const { Text } = Typography

// ── Design Tokens ──────────────────────────────────────────────────────────────
const PRIMARY = '#1677FF'
const SUCCESS = '#52C41A'
const DANGER = '#FF4D4F'
const WARNING = '#FAAD14'
const BORDER = '#E8ECF4'
const BG_EXPAND = '#F5F7FA'
const RADIUS = 10
const SHADOW = '0 2px 12px rgba(0,0,0,0.07)'

// ── Styles ─────────────────────────────────────────────────────────────────────
const SECTION_TITLE_STYLE = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 14,
}

const INFO_GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: '148px 1fr',
  gap: '0',
}

const FORM_ITEM_STYLE = { marginBottom: 12 }
const FLEX_ROW_GAP6 = { display: 'flex', gap: 6 }
const FLEX_COL_GAP4 = { display: 'flex', flexDirection: 'column', gap: 4 }
const FILE_LIST_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  minHeight: 24,
}

// ── Helpers ────────────────────────────────────────────────────────────────────
const scoreColor = score => {
  if (score == null) return '#d9d9d9'
  if (score >= 85) return SUCCESS
  if (score >= 60) return WARNING
  return DANGER
}

const toTitleCase = str =>
  str
    ? str
        .split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ')
    : str

// ── Sub-components ─────────────────────────────────────────────────────────────

const SectionTitle = ({ icon, title, extra }) => (
  <div style={SECTION_TITLE_STYLE}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ color: '#13C2C2', fontSize: 15 }}>{icon}</span>
      <Text strong style={{ fontSize: 14, color: '#1A1A2E' }}>
        {title}
      </Text>
    </div>
    {extra}
  </div>
)

const InfoRow = ({ label, children }) => (
  <div
    style={{
      ...INFO_GRID_STYLE,
      borderBottom: `1px solid ${BORDER}`,
      padding: '9px 0',
      fontSize: 13,
    }}>
    <div style={{ color: '#8C8C8C', fontWeight: 500, paddingRight: 8 }}>
      {label}
    </div>
    <div style={{ color: '#1A1A2E', wordBreak: 'break-word', fontWeight: 400 }}>
      {children == null || children === '' || children === false ? (
        <Text type="secondary">—</Text>
      ) : (
        children
      )}
    </div>
  </div>
)

const WeightBadge = ({ value, label, color = PRIMARY }) => {
  if (value == null) return null
  const displayValue = typeof value === 'number' ? value.toFixed(2) : value
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <Tag
        style={{
          borderRadius: 10,
          backgroundColor: `${color}14`,
          borderColor: `${color}35`,
          color: color,
          fontWeight: 700,
          fontSize: 12,
          padding: '2px 8px',
        }}>
        {displayValue}%
      </Tag>
    </div>
  )
}

const ScoreDisplay = ({ score }) => {
  if (score == null) return <Text type="secondary">Chưa có</Text>
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <Progress
        type="circle"
        percent={score}
        size={48}
        strokeColor={scoreColor(score)}
        format={p => (
          <span
            style={{ fontSize: 11, fontWeight: 700, color: scoreColor(score) }}>
            {p}
          </span>
        )}
      />
      <div>
        <div
          style={{ fontSize: 20, fontWeight: 800, color: scoreColor(score) }}>
          {score}
        </div>
        <Text style={{ fontSize: 11, color: '#8C8C8C' }}>/ 100 điểm</Text>
      </div>
    </div>
  )
}

const EditActionButtons = ({ saving, onCancel, onSave }) => (
  <div style={FLEX_ROW_GAP6}>
    <Button size="small" onClick={onCancel} style={{ borderRadius: 6 }}>
      Hủy
    </Button>
    <Button
      size="small"
      type="primary"
      loading={saving}
      onClick={onSave}
      style={{ borderRadius: 6 }}>
      Lưu
    </Button>
  </div>
)

const ROLE_SECTIONS = [
  {
    key: ASSIGNEE_TYPE.HANDLER,
    label: 'Người thực hiện',
    icon: <UserOutlined />,
  },
]

// ── Main Component ─────────────────────────────────────────────────────────────

const KpiV2SubDetailExpand = ({
  subKpiId,
  onDeleted,
  onUpdated,
  aspects,
  selectPeopleStore,
  fileStore,
  authenticationStore,
}) => {
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)

  const [isEditingInfo, setIsEditingInfo] = useState(false)
  const [isEditingAssignee, setIsEditingAssignee] = useState(false)
  const [isEditingFiles, setIsEditingFiles] = useState(false)
  const [isEditingResult, setIsEditingResult] = useState(false)
  const [infoSaving, setInfoSaving] = useState(false)
  const [assigneeSaving, setAssigneeSaving] = useState(false)
  const [fileSaving, setFileSaving] = useState(false)
  const [resultSaving, setResultSaving] = useState(false)

  const [infoForm] = Form.useForm()
  const [resultForm] = Form.useForm()
  const [fileList, setFileList] = useState([])

  const {
    selectUserData,
    selectDepartmentData,
    selectGroupData,
    isVisibleSelectUserDepartmentPopup,
    setIsVisibleSelectUserDepartmentPopup,
    handleOpenSelectUserDepartmentPopup,
    handleCancelSelectUserDepartment,
  } = selectPeopleStore

  // ── Fetch ────────────────────────────────────────────────────────────────────

  const fetchDetail = useCallback(async () => {
    if (!subKpiId) return
    setLoading(true)
    try {
      const res = await KpiV2Request.getSubKpiById(subKpiId)
      setDetail(res.data)
    } catch {
      message.error('Không thể tải chi tiết KPI cá nhân.')
    } finally {
      setLoading(false)
    }
  }, [subKpiId])

  useEffect(() => {
    fetchDetail()
  }, [fetchDetail])

  // ── Info edit ────────────────────────────────────────────────────────────────

  const handleStartEditInfo = () => {
    infoForm.setFieldsValue({
      ...detail,
      start_date: detail.start_date ? moment(detail.start_date) : null,
      end_date: detail.end_date ? moment(detail.end_date) : null,
      report_date_range: detail.report_date
        ? [
            moment(detail.report_date),
            detail.report_end_date ? moment(detail.report_end_date) : null,
          ]
        : null,
    })
    setIsEditingInfo(true)
  }

  const handleSaveInfo = async () => {
    try {
      const values = await infoForm.validateFields()
      setInfoSaving(true)
      const reportRange = values.report_date_range
      await KpiV2Request.updateSubKpi(subKpiId, {
        ...detail,
        attachment: (detail.attachment || []).map(f => f.file_id || f.id),
        ...values,
        report_date_range: undefined,
        start_date: values.start_date
          ? values.start_date.format('YYYY-MM-DD')
          : undefined,
        end_date: values.end_date
          ? values.end_date.format('YYYY-MM-DD')
          : undefined,
        report_date: reportRange?.[0]
          ? reportRange[0].format('YYYY-MM-DD')
          : undefined,
        report_end_date: reportRange?.[1]
          ? reportRange[1].format('YYYY-MM-DD')
          : undefined,
      })
      message.success('Cập nhật thông tin KPI cá nhân thành công!')
      setIsEditingInfo(false)
      fetchDetail()
      onUpdated?.()
    } catch (error) {
      if (error?.errorFields) return
      message.error(
        error?.response?.data?.message || 'Đã xảy ra lỗi, vui lòng thử lại.'
      )
    } finally {
      setInfoSaving(false)
    }
  }

  // ── Assignee edit ────────────────────────────────────────────────────────────

  const handleStartEditAssignee = () => {
    selectPeopleStore.clearSelectPeopleStore()
    selectPeopleStore.companyCode = authenticationStore.currentCompanyCode()
    selectPeopleStore.getDepartmentWithUsers()
    if (detail.assignees) {
      const newUserData = { ...selectPeopleStore.selectUserData }
      const newDeptData = { ...selectPeopleStore.selectDepartmentData }
      const newGroupData = { ...selectPeopleStore.selectGroupData }
      ROLE_SECTIONS.forEach(({ key }) => {
        const members = detail.assignees
          .filter(a => a.permission === key)
          .map(a => ({
            id: a.assignee_code,
            name: a.name_uppercase,
            full_name: a.name_uppercase,
            image_id: a.image,
            assignee_type: a.assignee_type,
          }))
        newUserData[key] = members.filter(
          a => a.assignee_type === ASSIGNEE_TYPE.USER
        )
        newDeptData[key] = members.filter(
          a => a.assignee_type === ASSIGNEE_TYPE.DEPARTMENT
        )
        newGroupData[key] = members.filter(
          a => a.assignee_type === ASSIGNEE_TYPE.GROUP
        )
      })
      selectPeopleStore.setSelectUserData(newUserData)
      selectPeopleStore.setSelectDepartmentData(newDeptData)
      selectPeopleStore.setSelectGroupData(newGroupData)
    }
    setIsEditingAssignee(true)
  }

  const getListByKey = key => [
    ...(selectUserData[key] || []),
    ...(selectDepartmentData[key] || []),
    ...(selectGroupData[key] || []),
  ]

  const handleSaveAssignees = async () => {
    try {
      setAssigneeSaving(true)
      const assignees = ROLE_SECTIONS.flatMap(({ key }) =>
        getListByKey(key).map(el => ({
          assignee_code: el.id,
          assignee_type: el.assignee_type || ASSIGNEE_TYPE.USER,
          permission: key,
        }))
      )
      await KpiV2Request.updateSubKpi(subKpiId, {
        ...detail,
        attachment: (detail.attachment || []).map(f => f.file_id || f.id),
        assignees,
      })
      message.success('Cập nhật người thực hiện thành công!')
      setIsEditingAssignee(false)
      selectPeopleStore.clearSelectPeopleStore()
      selectPeopleStore.companyCode = authenticationStore.currentCompanyCode()
      selectPeopleStore.getDepartmentWithUsers()
      await fetchDetail()
      onUpdated?.()
    } catch (error) {
      message.error(
        error?.response?.data?.message || 'Đã xảy ra lỗi, vui lòng thử lại.'
      )
    } finally {
      setAssigneeSaving(false)
    }
  }

  // ── File edit ────────────────────────────────────────────────────────────────

  const handleStartEditFiles = () => {
    setFileList(
      (detail.attachment || []).map(f => ({
        id: f.file_id || f.id,
        name: f.file_name || f.name,
        file_type: utils.getExtensionFile(f.file_name || f.name || ''),
      }))
    )
    setIsEditingFiles(true)
  }

  const handleFileUpload = async options => {
    const { file, onSuccess: onFileSuccess, onError: onFileError } = options
    const formData = new FormData()
    formData.append('file', file)
    try {
      const res = await fileStore.uploadFile(formData)
      const data = res.data
      setFileList(prev => [
        ...prev,
        {
          id: data.file_id || data.id,
          name: data.file_name || data.name,
          file_type: utils.getExtensionFile(data.file_name || data.name || ''),
        },
      ])
      onFileSuccess(res.data, file)
    } catch {
      message.error('Tải lên tệp tin thất bại.')
      onFileError(new Error('upload failed'))
    }
  }

  const handleSaveFiles = async () => {
    try {
      setFileSaving(true)
      await KpiV2Request.updateSubKpi(subKpiId, {
        ...detail,
        attachment: fileList.map(f => f.id),
      })
      message.success('Cập nhật tài liệu thành công!')
      setIsEditingFiles(false)
      fetchDetail()
    } catch (error) {
      message.error(
        error?.response?.data?.message || 'Đã xảy ra lỗi, vui lòng thử lại.'
      )
    } finally {
      setFileSaving(false)
    }
  }

  const handleDeleteFile = async fileId => {
    try {
      const remaining = (detail.attachment || [])
        .filter(f => (f.file_id || f.id) !== fileId)
        .map(f => f.file_id || f.id)
      await KpiV2Request.updateSubKpi(subKpiId, {
        ...detail,
        attachment: remaining,
      })
      message.success('Đã xóa tài liệu!')
      fetchDetail()
    } catch {
      message.error('Xóa tài liệu thất bại.')
    }
  }

  // ── Result edit ──────────────────────────────────────────────────────────────

  const handleStartEditResult = () => {
    resultForm.setFieldsValue({
      result: detail.result,
      score: detail.score,
      result_description: detail.result_description,
    })
    setIsEditingResult(true)
  }

  const handleSaveResult = async () => {
    try {
      const values = await resultForm.validateFields()
      setResultSaving(true)
      await KpiV2Request.updateSubKpi(subKpiId, {
        ...detail,
        attachment: (detail.attachment || []).map(f => f.file_id || f.id),
        result: values.result ?? null,
        score: values.score,
        result_description: values.result_description,
      })
      message.success('Cập nhật kết quả thành công!')
      setIsEditingResult(false)
      fetchDetail()
      onUpdated?.()
    } catch (error) {
      if (error?.errorFields) return
      message.error(
        error?.response?.data?.message || 'Đã xảy ra lỗi, vui lòng thử lại.'
      )
    } finally {
      setResultSaving(false)
    }
  }

  const handleDelete = async () => {
    try {
      await KpiV2Request.deleteSubKpi(subKpiId)
      message.success('Xóa KPI cá nhân thành công!')
      onDeleted?.()
    } catch {
      message.error('Xóa KPI cá nhân thất bại.')
    }
  }

  // ── Permission ────────────────────────────────────────────────────────────────

  const currentUsername = authenticationStore.currentUser?.username
  const isAdmin =
    authenticationStore.isAccountAdmin || authenticationStore.isSuperAdmin
  const canEdit =
    isAdmin ||
    (detail?.assignees || []).some(
      a =>
        a.assignee_code === currentUsername &&
        (a.permission === ASSIGNEE_TYPE.CREATE ||
          a.permission === ASSIGNEE_TYPE.HANDLER)
    )

  // ── Helpers ───────────────────────────────────────────────────────────────────

  const getAspectName = aspectId => {
    const asp = (aspects || []).find(a => a.id === aspectId)
    return asp ? asp.context : aspectId || ''
  }

  const formatEvaluation = v => {
    if (!v) return null
    return String(v)
      .split('\n')
      .map((line, i) => <div key={i}>{line}</div>)
  }

  const renderDateRange = () => {
    const s = detail.start_date
      ? moment(detail.start_date).format(DATE_FORMAT_DEFAULT)
      : null
    const e = detail.end_date
      ? moment(detail.end_date).format(DATE_FORMAT_DEFAULT)
      : null
    return (
      <span style={{ display: 'inline-flex', gap: 16, flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <CalendarOutlined style={{ color: PRIMARY }} />
          <Text style={{ color: '#8C8C8C', fontSize: 12 }}>Bắt đầu:</Text>
          <Text style={{ fontSize: 12 }}>
            {s || <i style={{ color: '#bbb' }}>Không rõ</i>}
          </Text>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <CalendarOutlined style={{ color: DANGER }} />
          <Text style={{ color: '#8C8C8C', fontSize: 12 }}>Kết thúc:</Text>
          <Text style={{ fontSize: 12 }}>
            {e || <i style={{ color: '#bbb' }}>Không rõ</i>}
          </Text>
        </span>
      </span>
    )
  }

  const renderReportDateRange = () => {
    if (!detail.report_date) return <Text type="secondary">—</Text>
    const start = moment(detail.report_date).format(DATE_FORMAT_DEFAULT)
    const end = detail.report_end_date
      ? moment(detail.report_end_date).format(DATE_FORMAT_DEFAULT)
      : null
    return (
      <Tag
        icon={<CalendarOutlined />}
        color="cyan"
        style={{ borderRadius: 10, fontSize: 12 }}>
        {end ? `${start} – ${end}` : start}
      </Tag>
    )
  }

  const renderUserTagList = (list, permission) =>
    list.map(el => (
      <Tag
        key={el.id}
        className="tag-selected"
        color="blue"
        closable
        style={{ borderRadius: 20, marginBottom: 4 }}
        onClose={() =>
          selectPeopleStore.handleRemoveSelect({ ...el, permission })
        }>
        <AvatarHasName
          imgId={el.image_id}
          size={20}
          name={el.full_name || el.name}
          icon={
            (el.assignee_type === ASSIGNEE_TYPE.DEPARTMENT && (
              <BankOutlined />
            )) ||
            (el.assignee_type === ASSIGNEE_TYPE.GROUP && <TeamOutlined />) || (
              <UserOutlined />
            )
          }
        />
      </Tag>
    ))

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <>
      <div
        style={{
          background: BG_EXPAND,
          padding: '16px 20px',
          borderTop: '3px solid #13C2C2',
        }}
        onClick={e => e.stopPropagation()}>
        <Spin spinning={loading}>
          {detail && (
            <Row gutter={[16, 16]}>
              {/* ── Left column: Thông tin chung ── */}
              <Col xs={24} lg={16}>
                <Card
                  style={{
                    borderRadius: RADIUS,
                    border: `1px solid ${BORDER}`,
                    boxShadow: SHADOW,
                  }}
                  bodyStyle={{ padding: '16px 20px' }}>
                  <SectionTitle
                    icon={<EditOutlined />}
                    title="Thông tin KPI Cá nhân"
                    extra={
                      isEditingInfo ? (
                        <EditActionButtons
                          saving={infoSaving}
                          onCancel={() => setIsEditingInfo(false)}
                          onSave={handleSaveInfo}
                        />
                      ) : canEdit ? (
                        <div style={FLEX_ROW_GAP6}>
                          <AccessControlAction
                            aclActionType={ACL_ACTION_TYPE.edit__KPI}>
                            <Button
                              size="small"
                              type="primary"
                              ghost
                              icon={<EditOutlined />}
                              style={{ borderRadius: 6 }}
                              onClick={handleStartEditInfo}>
                              Chỉnh sửa
                            </Button>
                          </AccessControlAction>
                          <AccessControlAction
                            aclActionType={ACL_ACTION_TYPE.delete__KPI}>
                            <Popconfirm
                              title="Bạn có chắc chắn muốn xóa KPI cá nhân này không?"
                              onConfirm={handleDelete}
                              okText="Xóa"
                              cancelText="Hủy"
                              okButtonProps={{ danger: true }}>
                              <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                style={{ borderRadius: 6 }}>
                                Xóa
                              </Button>
                            </Popconfirm>
                          </AccessControlAction>
                        </div>
                      ) : null
                    }
                  />

                  {isEditingInfo ? (
                    <Form
                      form={infoForm}
                      layout="horizontal"
                      labelCol={{ flex: '0 0 160px' }}
                      wrapperCol={{ flex: 1 }}
                      labelAlign="left"
                      colon={false}>
                      <Form.Item
                        name="aspect_id"
                        label="Khía cạnh"
                        style={FORM_ITEM_STYLE}>
                        <Select
                          placeholder="Chọn khía cạnh"
                          allowClear
                          style={{ borderRadius: 8 }}>
                          {(aspects || []).map(asp => (
                            <Option key={asp.id} value={asp.id}>
                              {asp.context}
                              {asp.weight != null ? ` (${asp.weight}%)` : ''}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        name="purpose"
                        label="Mục tiêu"
                        style={FORM_ITEM_STYLE}>
                        <Input
                          placeholder="Nhập nội dung"
                          style={{ borderRadius: 8 }}
                        />
                      </Form.Item>
                      <Form.Item name="kgi" label="KGI" style={FORM_ITEM_STYLE}>
                        <Input
                          placeholder="Nhập nội dung"
                          style={{ borderRadius: 8 }}
                        />
                      </Form.Item>
                      <Form.Item name="csf" label="CSF" style={FORM_ITEM_STYLE}>
                        <Input
                          placeholder="Nhập nội dung"
                          style={{ borderRadius: 8 }}
                        />
                      </Form.Item>
                      <Form.Item name="kpi" label="KPI" style={FORM_ITEM_STYLE}>
                        <Input
                          placeholder="Nhập nội dung"
                          style={{ borderRadius: 8 }}
                        />
                      </Form.Item>
                      <Form.Item
                        name="measurement"
                        label="Đơn vị tính"
                        style={FORM_ITEM_STYLE}>
                        <Input
                          placeholder="VD: %, VNĐ..."
                          style={{ borderRadius: 8 }}
                        />
                      </Form.Item>
                      <Form.Item
                        name="indicator_weight"
                        label="Trọng số chỉ tiêu"
                        style={FORM_ITEM_STYLE}>
                        <InputNumber
                          min={0}
                          max={100}
                          placeholder="0-100"
                          addonAfter="%"
                          style={{ width: '100%', borderRadius: 8 }}
                          onChange={value => {
                            if (value != null && detail?.aspect_id) {
                              const aspect = aspects.find(
                                a => a.id === detail.aspect_id
                              )
                              if (aspect && aspect.weight) {
                                const overallWeight =
                                  (value * aspect.weight) / 100
                                infoForm.setFieldsValue({
                                  overall_weight: overallWeight,
                                })
                              } else {
                                infoForm.setFieldsValue({
                                  overall_weight: value,
                                })
                              }
                            }
                          }}
                        />
                      </Form.Item>
                      <Form.Item
                        name="overall_weight"
                        label="Trọng số chung"
                        style={FORM_ITEM_STYLE}>
                        <InputNumber
                          min={0}
                          max={100}
                          placeholder="0-100"
                          addonAfter="%"
                          style={{ width: '100%', borderRadius: 8 }}
                        />
                      </Form.Item>
                      <Form.Item
                        name="evaluation_rule"
                        label="Quy ước đánh giá"
                        style={FORM_ITEM_STYLE}>
                        <TextArea
                          rows={3}
                          placeholder="Nhập quy ước đánh giá..."
                          style={{ borderRadius: 8 }}
                        />
                      </Form.Item>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="start_date"
                            label="Ngày bắt đầu"
                            labelCol={{ span: 24 }}
                            wrapperCol={{ span: 24 }}
                            style={FORM_ITEM_STYLE}>
                            <DatePicker
                              format="DD/MM/YYYY"
                              style={{ width: '100%', borderRadius: 8 }}
                              suffixIcon={<CalendarOutlined />}
                              placeholder="dd/mm/yyyy"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="end_date"
                            label="Hạn hoàn thành"
                            labelCol={{ span: 24 }}
                            wrapperCol={{ span: 24 }}
                            style={FORM_ITEM_STYLE}>
                            <DatePicker
                              format="DD/MM/YYYY"
                              style={{ width: '100%', borderRadius: 8 }}
                              suffixIcon={<CalendarOutlined />}
                              placeholder="dd/mm/yyyy"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item
                        name="report_date_range"
                        label="Thời gian báo cáo"
                        style={FORM_ITEM_STYLE}>
                        <DatePicker.RangePicker
                          format="DD/MM/YYYY"
                          style={{ width: '100%', borderRadius: 8 }}
                          suffixIcon={<CalendarOutlined />}
                          placeholder={['Từ ngày', 'Đến ngày']}
                        />
                      </Form.Item>
                    </Form>
                  ) : (
                    <>
                      <InfoRow label="Thời gian">{renderDateRange()}</InfoRow>
                      <InfoRow label="Thời gian báo cáo">
                        {renderReportDateRange()}
                      </InfoRow>
                      <InfoRow label="Khía cạnh">
                        {getAspectName(detail.aspect_id) && (
                          <Tag color="cyan" style={{ borderRadius: 10 }}>
                            {getAspectName(detail.aspect_id)}
                          </Tag>
                        )}
                      </InfoRow>
                      <InfoRow label="Mục tiêu">{detail.purpose}</InfoRow>
                      <InfoRow label="KGI">{detail.kgi}</InfoRow>
                      <InfoRow label="CSF">{detail.csf}</InfoRow>
                      <InfoRow label="KPI">
                        {detail.kpi && (
                          <Tag
                            color="cyan"
                            style={{ borderRadius: 10, fontWeight: 500 }}>
                            {detail.kpi}
                          </Tag>
                        )}
                      </InfoRow>
                      <InfoRow label="Đơn vị tính">
                        {detail.measurement && (
                          <Tag style={{ borderRadius: 8 }}>
                            {detail.measurement}
                          </Tag>
                        )}
                      </InfoRow>
                      <InfoRow label="Trọng số chỉ tiêu">
                        {detail.indicator_weight != null && (
                          <WeightBadge
                            value={detail.indicator_weight}
                            label="Trọng số chỉ tiêu"
                            color="#13C2C2"
                          />
                        )}
                      </InfoRow>
                      <InfoRow label="Trọng số chung">
                        {detail.overall_weight != null && (
                          <WeightBadge
                            value={detail.overall_weight}
                            label="Trọng số chung"
                            color="#722ED1"
                          />
                        )}
                      </InfoRow>
                      <InfoRow label="Quy ước đánh giá">
                        {formatEvaluation(detail.evaluation_rule)}
                      </InfoRow>
                    </>
                  )}
                </Card>
              </Col>

              {/* ── Right column ── */}
              <Col xs={24} lg={8}>
                {/* Người thực hiện */}
                <Card
                  style={{
                    borderRadius: RADIUS,
                    border: `1px solid ${BORDER}`,
                    boxShadow: SHADOW,
                    marginBottom: 14,
                  }}
                  bodyStyle={{ padding: '16px 20px' }}>
                  <SectionTitle
                    icon={<UserOutlined />}
                    title="Người thực hiện"
                    extra={
                      isEditingAssignee ? (
                        <EditActionButtons
                          saving={assigneeSaving}
                          onCancel={() => {
                            setIsEditingAssignee(false)
                            selectPeopleStore.clearSelectPeopleStore()
                          }}
                          onSave={handleSaveAssignees}
                        />
                      ) : canEdit ? (
                        <Button
                          size="small"
                          type="primary"
                          ghost
                          icon={<UserOutlined />}
                          style={{ borderRadius: 6 }}
                          onClick={handleStartEditAssignee}>
                          Phân bổ
                        </Button>
                      ) : null
                    }
                  />

                  {isEditingAssignee ? (
                    <div style={FLEX_COL_GAP4}>
                      {ROLE_SECTIONS.map(({ key, label, icon }) => (
                        <div key={key}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: 8,
                            }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                fontSize: 12,
                                fontWeight: 600,
                                color: '#555',
                              }}>
                              {icon} {label}
                            </div>
                            <Tooltip
                              title={`Chọn ${label.toLowerCase()}`}
                              color={blue}>
                              <Button
                                size="small"
                                icon={<UserOutlined style={{ color: blue }} />}
                                style={{ borderRadius: 6 }}
                                onClick={() =>
                                  handleOpenSelectUserDepartmentPopup(key)
                                }
                              />
                            </Tooltip>
                          </div>
                          <SelectList
                            style={{ maxHeight: 'none', overflowY: 'visible' }}>
                            {renderUserTagList(getListByKey(key), key)}
                          </SelectList>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={FLEX_COL_GAP4}>
                      {ROLE_SECTIONS.map(({ key, label, icon }) => {
                        const members = (detail.assignees || []).filter(
                          a => a.permission === key
                        )
                        return (
                          <div key={key}>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: '#8C8C8C',
                                marginBottom: 8,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                              }}>
                              {icon} {label}
                            </div>
                            {members.length > 0 ? (
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 6,
                                }}>
                                {members.map((a, idx) => (
                                  <div
                                    key={idx}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 8,
                                      padding: '6px 10px',
                                      background: '#F0FFFE',
                                      borderRadius: 8,
                                      border: '1px solid #B5F0EE',
                                    }}>
                                    <AvatarHasName
                                      imgId={a.image}
                                      name={a.name_uppercase}
                                      size={28}
                                    />
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <SelectList
                                style={{
                                  maxHeight: 'none',
                                  overflowY: 'visible',
                                }}>
                                <PopoverWatchTime
                                  userList={members.map(a => ({
                                    id: a.assignee_code,
                                    full_name: toTitleCase(a.name_uppercase),
                                    assignee_type: a.assignee_type,
                                    permission: a.permission,
                                  }))}
                                  title={label}
                                  isFullViewer={true}
                                  typeId={subKpiId}
                                  compared_date={
                                    VIEWLOG_DATE.internal_document_until
                                  }
                                  created_at={detail.created_at || ''}
                                />
                              </SelectList>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </Card>

                {/* Tài liệu đính kèm */}
                <Card
                  style={{
                    borderRadius: RADIUS,
                    border: `1px solid ${BORDER}`,
                    boxShadow: SHADOW,
                    marginBottom: 14,
                  }}
                  bodyStyle={{ padding: '16px 20px' }}>
                  <SectionTitle
                    icon={<FileOutlined />}
                    title="Tài liệu đính kèm"
                    extra={
                      isEditingFiles ? (
                        <EditActionButtons
                          saving={fileSaving}
                          onCancel={() => setIsEditingFiles(false)}
                          onSave={handleSaveFiles}
                        />
                      ) : canEdit ? (
                        <Button
                          size="small"
                          type="primary"
                          ghost
                          icon={<CloudUploadOutlined />}
                          style={{ borderRadius: 6 }}
                          onClick={handleStartEditFiles}>
                          Thêm
                        </Button>
                      ) : null
                    }
                  />

                  {isEditingFiles ? (
                    <>
                      <Upload
                        customRequest={handleFileUpload}
                        showUploadList={false}>
                        <Button
                          icon={<CloudUploadOutlined />}
                          style={{ marginBottom: 10, borderRadius: 8 }}>
                          Chọn tệp
                        </Button>
                      </Upload>
                      <div style={FLEX_COL_GAP4}>
                        {fileList.map(f => (
                          <UploadFileItem
                            key={f.id}
                            file_id={f.id}
                            file_name={f.name}
                            file_type={f.file_type}
                            handleRemoveFileFromUploadList={() =>
                              setFileList(prev =>
                                prev.filter(x => x.id !== f.id)
                              )
                            }
                          />
                        ))}
                      </div>
                    </>
                  ) : (
                    <div style={FILE_LIST_STYLE}>
                      {(detail.attachment || []).length === 0 && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Chưa có tài liệu
                        </Text>
                      )}
                      {(detail.attachment || []).map(f => (
                        <FileItem
                          key={f.file_id}
                          file_id={f.file_id}
                          file_name={f.file_name}
                          file_type={utils.getExtensionFile(f.file_name || '')}
                          onDelete={() => handleDeleteFile(f.file_id || f.id)}
                        />
                      ))}
                    </div>
                  )}
                </Card>

                {/* Kết quả & Đánh giá */}
                <Card
                  style={{
                    borderRadius: RADIUS,
                    border: `1px solid ${BORDER}`,
                    boxShadow: SHADOW,
                  }}
                  bodyStyle={{ padding: '16px 20px' }}>
                  <SectionTitle
                    icon={<TrophyOutlined />}
                    title="Kết quả & Đánh giá"
                    extra={
                      isEditingResult ? (
                        <EditActionButtons
                          saving={resultSaving}
                          onCancel={() => setIsEditingResult(false)}
                          onSave={handleSaveResult}
                        />
                      ) : canEdit ? (
                        <Button
                          size="small"
                          type="primary"
                          ghost
                          icon={<CheckCircleOutlined />}
                          style={{ borderRadius: 6 }}
                          onClick={handleStartEditResult}>
                          Cập nhật
                        </Button>
                      ) : null
                    }
                  />

                  {isEditingResult ? (
                    <Form form={resultForm} layout="vertical" colon={false}>
                      <Form.Item
                        name="result"
                        label="Kết quả thực hiện"
                        style={FORM_ITEM_STYLE}>
                        <Input
                          placeholder="Nhập kết quả thực hiện..."
                          style={{ borderRadius: 8 }}
                        />
                      </Form.Item>
                      <Form.Item
                        name="score"
                        label="Điểm"
                        style={FORM_ITEM_STYLE}>
                        <InputNumber
                          min={0}
                          max={100}
                          style={{ width: '100%', borderRadius: 8 }}
                          placeholder="0 - 100"
                        />
                      </Form.Item>
                      <Form.Item
                        name="result_description"
                        label="Thuyết minh kết quả"
                        style={FORM_ITEM_STYLE}>
                        <TextArea
                          rows={3}
                          placeholder="Nhập thuyết minh..."
                          style={{ borderRadius: 8 }}
                        />
                      </Form.Item>
                    </Form>
                  ) : (
                    <>
                      {detail.score != null && (
                        <div style={{ marginBottom: 16 }}>
                          <ScoreDisplay score={detail.score} />
                        </div>
                      )}
                      <InfoRow label="Kết quả">{detail.result}</InfoRow>
                      <InfoRow label="Thuyết minh">
                        {detail.result_description}
                      </InfoRow>
                    </>
                  )}
                </Card>
              </Col>
            </Row>
          )}
        </Spin>
      </div>

      <SelectPeoplePopup
        onlyOneUser={
          selectPeopleStore.assigneePopupType === ASSIGNEE_TYPE.HANDLER
        }
        handleCancelSelectUser={handleCancelSelectUserDepartment}
        handleSubmitSelectUser={() =>
          setIsVisibleSelectUserDepartmentPopup(false)
        }
        isVisibleSelectPeoplePopup={isVisibleSelectUserDepartmentPopup}
      />
    </>
  )
}

export default inject(
  'selectPeopleStore',
  'fileStore',
  'authenticationStore'
)(observer(KpiV2SubDetailExpand))
